import first from "lodash/first";
import { Fragment, type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useCurrentPageType, useTrackImpressionEventByIntersection } from "shared/hooks";
import type { PageType } from "shared/lib/page-type";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { opinionHomeV2QueueQuery$key } from "scmp-app/queries/__generated__/opinionHomeV2QueueQuery.graphql";

import {
  Arrow,
  Container,
  Divider,
  Item1,
  Item2,
  Item3,
  Item4,
  Item5,
  Item6,
  ItemContainer,
  ItemHarry,
  MoreLink,
  MoreLinkContainer,
  Title,
} from "./styles";

type Props = {
  className?: string;
  reference: opinionHomeV2QueueQuery$key;
};

export const HomeV2Opinion: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment opinionHomeV2QueueQuery on Query
      @argumentDefinitions(
        commentHarrySectionIds: { type: "[String]" }
        commentLimit: { type: "Int", defaultValue: 6 }
        commentQueueName: { type: "String!" }
        harrysViewLimit: { type: "Int", defaultValue: 1 }
        scmpPlusPaywallTypeIds: { type: "[String]", defaultValue: [] }
        withHarrysView: { type: "Boolean", defaultValue: true }
      ) {
        commentSection: section(filter: { entityId: "93" }) {
          urlAlias
        }
        opinionQueue: queue(filter: { name: $commentQueueName }) {
          items(first: $commentLimit) {
            edges {
              node {
                ... on Content {
                  entityId
                  ...commentV2ContentItemContent
                }
              }
            }
          }
        }
        harryContents: contents(
          contentTypes: [GALLERY]
          first: $harrysViewLimit
          filter: { sectionIds: $commentHarrySectionIds }
          articleExcludeInput: { paywallTypeIds: $scmpPlusPaywallTypeIds }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
        ) @include(if: $withHarrysView) {
          edges {
            node {
              ... on Content {
                entityId
                ...harryContentItemContent
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const commentItems = data?.opinionQueue?.items?.edges ?? [];
  const listOfArticles = commentItems.map(item => item.node.entityId).join(",");

  const harryContent = first(data?.harryContents?.edges);
  const opinionSectionUrl = data?.commentSection?.urlAlias ?? "/comment";

  /** TODO: find a way to restrict page type in a shared component */
  const currentPageType = useCurrentPageType() as
    | PageType.Homepage
    | PageType.Section
    | PageType.SubSection;

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: "Opinion",
        list_of_articles: listOfArticles,
        page_type: currentPageType,
        widget_name: "homepage_section_block",
      },
      subcategory: "widget",
    }),
    [currentPageType, listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  const sendTrackingHandler = (entityId?: string, urlAlias?: string) => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        block_section: "Opinion",
        destination_url: getAbsoluteUrl(urlAlias),
        page_type: currentPageType,
        widget_name: "homepage_section_block",
      },
      subcategory: "widget",
    });
  };

  const itemComponents = [Item1, Item2, Item3, Item4, Item5, Item6];

  if (commentItems.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.Opinion,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title pathname={opinionSectionUrl}>Opinion</Title>
        <ItemContainer>
          {commentItems.map(({ node }, index) => {
            const Component = itemComponents[index];
            return (
              <Fragment key={node.entityId}>
                <Component
                  onClick={(entityId, urlAlias) => {
                    sendTrackingHandler(entityId, urlAlias);
                  }}
                  reference={node}
                />
                {index === 3 && <Divider />}
              </Fragment>
            );
          })}
          {harryContent && (
            <ItemHarry
              onClick={(entityId, urlAlias) => {
                sendTrackingHandler(entityId, urlAlias);
              }}
              reference={harryContent.node}
            />
          )}
        </ItemContainer>
        <MoreLinkContainer>
          <MoreLink pathname={opinionSectionUrl}>
            More Opinion
            <Arrow />
          </MoreLink>
        </MoreLinkContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Opinion.displayName = "HomeV2Opinion";
